import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useTheme } from '../../../theme';
import { resolveNeighborhoodColorScale } from '../../../theme/resolveNeighborhoodColor';
const STYLE_ID = 'loci-affinity-ring-keyframes';
const DRAW_DURATION_MS = 800;
const VIEWBOX = 80;
const DEFAULT_SIZE = 80;
const DEFAULT_STROKE_WIDTH = 6;
const AFFINITY_KEYFRAMES = `
  @keyframes fs-affinity-draw {
    from { stroke-dashoffset: var(--fs-affinity-dash-start, 0); }
    to { stroke-dashoffset: var(--fs-affinity-dash-end, 0); }
  }
`;
// Mirrors LociO pattern — inject the keyframes once via useInsertionEffect so
// the style lands before the first paint. Falls back to useLayoutEffect on
// older React builds (belt-and-braces for SSR-only environments).
const useInsertionEffectFallback = React.useInsertionEffect ??
    React.useLayoutEffect;
const useAffinityKeyframes = () => {
    useInsertionEffectFallback(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ID))
            return;
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = AFFINITY_KEYFRAMES;
        document.head.appendChild(style);
    }, []);
};
const useReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => typeof window !== 'undefined'
        ? (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false)
        : false);
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq)
            return;
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return prefersReducedMotion;
};
const clampPercentage = (value) => {
    if (Number.isNaN(value))
        return 0;
    if (value < 0)
        return 0;
    if (value > 100)
        return 100;
    return value;
};
const AffinityRingBase = ({ percentage, neighborhood, neighborhoodLabel, size = DEFAULT_SIZE, strokeWidth = DEFAULT_STROKE_WIDTH, animate = true, className, style, 'aria-label': ariaLabelOverride, ...rest }) => {
    useAffinityKeyframes();
    const theme = useTheme();
    const prefersReducedMotion = useReducedMotion();
    const pct = clampPercentage(Math.round(percentage));
    const radius = (VIEWBOX - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - pct / 100);
    const strokeColor = resolveNeighborhoodColorScale(neighborhood, theme.neighborhood).primary;
    // Track color sits on top of the page surface — surfaceAlt gives a subtle
    // background circle that reads in both themes without competing with the
    // foreground stroke.
    const trackColor = theme.colors.surfaceAlt;
    const shouldAnimate = animate && !prefersReducedMotion;
    const ariaLabel = ariaLabelOverride ?? `${neighborhoodLabel}: ${pct}% of your saved events`;
    const foregroundStyle = useMemo(() => {
        const base = {
            // Keep the arc starting at 12 o'clock — SVG circles start at 3 o'clock
            // by default; a -90deg rotation makes the progress feel natural.
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
            transition: shouldAnimate ? undefined : `stroke-dashoffset 0ms`,
        };
        if (shouldAnimate) {
            return {
                ...base,
                // Expose dash endpoints to the keyframe so the animation is
                // parameterized per-instance without a unique @keyframes rule.
                ['--fs-affinity-dash-start']: `${circumference}`,
                ['--fs-affinity-dash-end']: `${dashOffset}`,
                animation: `fs-affinity-draw ${DRAW_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
            };
        }
        return base;
    }, [shouldAnimate, circumference, dashOffset]);
    const textStyle = {
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: 20,
        fontWeight: theme.typography.fontWeight.semibold,
        fill: theme.colors.text,
    };
    return (_jsxs("svg", { width: size, height: size, viewBox: `0 0 ${VIEWBOX} ${VIEWBOX}`, role: "img", "aria-label": ariaLabel, className: className, style: style, "data-testid": "affinity-ring", "data-neighborhood": neighborhood, "data-percentage": pct, ...rest, children: [_jsx("circle", { cx: VIEWBOX / 2, cy: VIEWBOX / 2, r: radius, fill: "none", stroke: trackColor, strokeWidth: strokeWidth, "data-testid": "affinity-ring-track" }), _jsx("circle", { cx: VIEWBOX / 2, cy: VIEWBOX / 2, r: radius, fill: "none", stroke: strokeColor, strokeWidth: strokeWidth, strokeLinecap: "round", strokeDasharray: circumference, strokeDashoffset: dashOffset, style: foregroundStyle, "data-testid": "affinity-ring-foreground" }), _jsxs("text", { x: "50%", y: "50%", textAnchor: "middle", dominantBaseline: "central", style: textStyle, "data-testid": "affinity-ring-label", children: [pct, "%"] })] }));
};
export const AffinityRing = React.memo(AffinityRingBase);
AffinityRing.displayName = 'AffinityRing';
